import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  TableContainer,
  Table,
  TableData,
  TableRow,
  FilterContainer,
  ClearFilters,
  DropdownToggleStyled,
  DropdownItemContent,
  IconWrapper,
  ButtonSeparator
} from './layout';
import { ACTION_COLUMNS, useColumnOrder } from './hooks/useColumnOrder';

import { AiOutlineClear } from 'react-icons/ai';
import { DraggableTh } from './components/draggableTh';
import { Dropdown, DropdownItem, DropdownMenu } from 'reactstrap';
import { BiFilterAlt } from 'react-icons/bi';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { tableTheme } from './layout/theme';

/**
 * @typedef {Object} Column
 * @property {string} dataField
 * @property {string} text
 * @property {Function} [formatter]
 * @property {Object} [style]
 * @property {Object} [headerStyle]
 * @property {boolean} [sort]
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} key
 * @property {string} direction
 */

/**
 * @param {Object} props
 * @param {string} [props.entityName]
 * @param {string} [props.classes]
 * @param {Function} props.setSortConfig
 * @param {SortConfig} props.sortConfig
 * @param {Column[]} props.columns
 * @param {Object[]} props.data
 * @param {string} props.keyField
 * @param {boolean} [props.bootstrap4]
 * @param {boolean} [props.bordered]
 * @param {string} [props.rowClasses]
 */
const FlexTable = ({
  classes,
  setSortConfig,
  sortConfig,
  entityName,
  ...props
}) => {
  const { columns } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {
    columnOrder,
    columnWidths,
    handleResize,
    moveColumn,
    requestSort,
    resetFilter,
    visibleColumns,
    handleColumnVisibilityChange
  } = useColumnOrder(columns, setSortConfig, entityName);

  const sortedData = useMemo(() => {
    if (!sortConfig?.key) return props.data;

    const sorted = [...props.data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      const aVal = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
      const bVal = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

      if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [props.data, sortConfig]);

  const handleResizeColumn = useCallback((index, width) => {
    handleResize(index, width);
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <FilterContainer>
        <ClearFilters
          onClick={() => {
            resetFilter();
          }}
          className="small"
        >
          <AiOutlineClear /> Limpar visualização
        </ClearFilters>
        <ButtonSeparator />
        <Dropdown
          direction="right"
          onClick={() => {
            setDropdownOpen(!dropdownOpen);
          }}
          isOpen={dropdownOpen}
          toggle={() => setDropdownOpen(!dropdownOpen)}
        >
          <DropdownToggleStyled outline={true}>
            <BiFilterAlt size={16} color={tableTheme.colors.primary} />
          </DropdownToggleStyled>

          <DropdownMenu onClick={(e) => e.stopPropagation()} positionFixed>
            <DropdownItem header>Visualizar colunas</DropdownItem>
            <DropdownItem divider />
            {columns
              .filter((col) => !ACTION_COLUMNS.includes(col.dataField))
              .map((col) => (
                <DropdownItem
                  key={col.dataField}
                  toggle={() => setDropdownOpen(!dropdownOpen)}
                  onClick={(e) => {
                    handleColumnVisibilityChange(col.dataField);
                    e.stopPropagation();
                  }}
                >
                  <DropdownItemContent
                    onClick={(e) => {
                      handleColumnVisibilityChange(col.dataField);
                      e.stopPropagation();
                    }}
                  >
                    {visibleColumns.includes(col.dataField) ? (
                      <LuEye
                        onClick={(e) => {
                          handleColumnVisibilityChange(col.dataField);
                          e.stopPropagation();
                        }}
                      />
                    ) : (
                      <LuEyeOff
                        onClick={(e) => {
                          handleColumnVisibilityChange(col.dataField);
                          e.stopPropagation();
                        }}
                      />
                    )}
                    <IconWrapper
                      onClick={(e) => {
                        handleColumnVisibilityChange(col.dataField);
                        e.stopPropagation();
                      }}
                    >
                      {col.text}
                    </IconWrapper>
                  </DropdownItemContent>
                </DropdownItem>
              ))}
          </DropdownMenu>
        </Dropdown>
      </FilterContainer>
      <TableContainer>
        <Table className={classes}>
          <thead>
            <TableRow>
              {columnOrder.map(
                (column, index) =>
                  (visibleColumns.includes(column.dataField) ||
                    ACTION_COLUMNS.includes(column.dataField)) && (
                    <DraggableTh
                      key={column.dataField}
                      column={column}
                      index={index}
                      moveColumn={moveColumn}
                      requestSort={requestSort}
                      sortConfig={sortConfig}
                      onResize={handleResizeColumn}
                      columnWidths={columnWidths}
                      columnOrder={columnOrder}
                    >
                      {column.text}
                    </DraggableTh>
                  )
              )}
            </TableRow>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <TableRow key={row[props.keyField] || rowIndex}>
                {columnOrder.map(
                  (column, colIndex) =>
                    (visibleColumns.includes(column.dataField) ||
                      ACTION_COLUMNS.includes(column.dataField)) && (
                      <TableData
                        key={column.dataField}
                        style={{
                          ...column.style,
                          width: ACTION_COLUMNS.includes(column.dataField)
                            ? 80
                            : columnWidths[colIndex]
                        }}
                      >
                        {column.formatter
                          ? column.formatter(row[column.dataField], row)
                          : row[column.dataField]}
                      </TableData>
                    )
                )}
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </DndProvider>
  );
};

FlexTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataField: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      formatter: PropTypes.func,
      style: PropTypes.object,
      headerStyle: PropTypes.object,
      sort: PropTypes.bool
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  keyField: PropTypes.string.isRequired,
  bootstrap4: PropTypes.bool,
  bordered: PropTypes.bool,
  classes: PropTypes.string,
  rowClasses: PropTypes.string,
  setSortConfig: PropTypes.func.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.string
  }).isRequired
};

export default FlexTable;
